import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../../components/feature/Header';
import Footer from '../../../../components/feature/Footer';

const GoogleMyBusinessSetup: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50">
      <Header />
      
      <article className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Article Header */}
          <div className="mb-12">
            <Link to="/blog" className="inline-flex items-center gap-2 text-teal-600 font-semibold hover:text-teal-700 mb-6 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Blog
            </Link>
            <div className="mb-6">
              <span className="bg-teal-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                How To's
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              How to Set Up Your Google My Business Profile for Maximum Visibility
            </h1>
            <div className="flex items-center gap-6 text-gray-600 mb-8">
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                March 12, 2024
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                8 min read
              </span>
            </div>
            <div className="relative w-full h-96 rounded-2xl overflow-hidden mb-8">
              <img
                src="https://public.readdy.ai/ai/img_res/3303507385fd27f00ae269095c8c607e.jpg"
                alt="Google My Business Setup"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              Your Google My Business (now called Google Business Profile) listing is one of the most powerful free marketing tools available to local businesses. When optimized correctly, it can dramatically increase your visibility in local search results, drive more foot traffic to your location, and generate valuable leads. This comprehensive guide will walk you through every step of setting up and optimizing your profile for maximum impact.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step 1: Claim or Create Your Business Profile</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Start by searching for your business on Google Maps. If your business already appears, you can claim it. If not, you'll need to create a new listing. Go to google.com/business and click "Manage now." Sign in with your Google account and follow the prompts to add your business name and category.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              <strong>Pro Tip:</strong> Choose your primary business category carefully—it significantly impacts when and where your business appears in search results. Select the most specific category that accurately describes your business.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step 2: Verify Your Business</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Google requires verification to ensure listing accuracy. Most businesses receive a postcard with a verification code at their physical address within 5-7 days. Some businesses may qualify for instant verification via phone, email, or video. Complete this step promptly—your listing won't appear in search results until verified.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step 3: Complete Your Business Information</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Fill out every section of your profile completely and accurately:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li><strong>Business Name:</strong> Use your actual business name without keyword stuffing</li>
              <li><strong>Address:</strong> Ensure it matches your website and other online directories exactly</li>
              <li><strong>Phone Number:</strong> Use a local number that customers can call directly</li>
              <li><strong>Website URL:</strong> Link to your homepage or a dedicated landing page</li>
              <li><strong>Hours of Operation:</strong> Keep these updated, including special hours for holidays</li>
              <li><strong>Business Description:</strong> Write a compelling 750-character description highlighting what makes your business unique</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step 4: Add High-Quality Photos</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Businesses with photos receive 42% more requests for directions and 35% more click-throughs to their websites. Upload:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Logo (square format, at least 720x720 pixels)</li>
              <li>Cover photo (landscape format, at least 1024x576 pixels)</li>
              <li>Interior and exterior photos of your business</li>
              <li>Photos of your products or services</li>
              <li>Team photos to add a personal touch</li>
              <li>Action shots showing your business in operation</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-6">
              Aim for at least 10-15 high-quality photos initially, and add new photos monthly to keep your profile fresh.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step 5: Select Relevant Attributes</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Attributes help customers understand what to expect from your business. Select all that apply, such as "wheelchair accessible," "free Wi-Fi," "outdoor seating," "women-led," or "LGBTQ+ friendly." These details help customers make informed decisions and can set you apart from competitors.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step 6: Add Products and Services</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Showcase what you offer by adding products and services with descriptions, prices, and photos. This feature is especially valuable for restaurants (menu items), retail stores (products), and service businesses (service packages). Update this section regularly to reflect current offerings and seasonal specials.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step 7: Enable Messaging</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Turn on messaging to allow customers to contact you directly through your Google Business Profile. Download the Google Business Profile app to receive and respond to messages promptly. Quick response times improve customer satisfaction and can positively impact your local search rankings.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step 8: Create Posts Regularly</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Google Posts appear in your Business Profile and can highlight offers, events, products, or updates. Create posts weekly to keep your profile active and engaging. Include eye-catching images, clear calls-to-action, and relevant information that encourages customer interaction.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step 9: Encourage and Respond to Reviews</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Reviews are crucial for local SEO and customer trust. Develop a strategy to encourage satisfied customers to leave reviews. Respond to all reviews—both positive and negative—professionally and promptly. Thank customers for positive feedback and address concerns in negative reviews constructively.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              <strong>Important:</strong> Never offer incentives for reviews, as this violates Google's policies and can result in penalties.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step 10: Monitor Insights and Optimize</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Use the Insights section to track how customers find and interact with your listing. Monitor metrics like search queries, views, clicks, calls, and direction requests. Use this data to refine your strategy and identify opportunities for improvement.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Common Mistakes to Avoid</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Inconsistent NAP (Name, Address, Phone) information across platforms</li>
              <li>Choosing the wrong primary business category</li>
              <li>Neglecting to update hours during holidays or special events</li>
              <li>Ignoring customer reviews or responding defensively</li>
              <li>Using low-quality or irrelevant photos</li>
              <li>Keyword stuffing in your business name or description</li>
              <li>Creating duplicate listings</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Conclusion</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Setting up and optimizing your Google Business Profile is an ongoing process, not a one-time task. Regularly update your information, add fresh photos, create posts, and engage with customer reviews. A well-maintained profile can significantly boost your local visibility and drive more customers to your business.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              Remember, your GMB profile is often the first impression potential customers have of your business. Make it count by keeping it complete, accurate, and engaging. The effort you put into optimizing your profile will pay dividends in increased visibility and customer trust.
            </p>

            {/* Author Bio */}
            <div className="bg-gray-100 rounded-2xl p-8 mt-12 mb-8">
              <div className="flex items-start gap-6">
                <div className="w-24 h-24 flex items-center justify-center bg-teal-600 rounded-full flex-shrink-0">
                  <span className="text-3xl font-bold text-white">KH</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Kimberly Herring</h3>
                  <p className="text-teal-600 font-semibold mb-3">Founder, Digital Experience Studio</p>
                  <p className="text-gray-700 leading-relaxed">
                    Kimberly has helped hundreds of local businesses optimize their Google My Business profiles to dominate local search results. She specializes in local SEO strategies that drive foot traffic and phone calls.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact CTA */}
            <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl p-8 text-white mt-12">
              <h3 className="text-2xl font-bold mb-4">Need Help Optimizing Your Google My Business Profile?</h3>
              <p className="text-teal-100 mb-6">
                Let us handle your GMB optimization so you can focus on running your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <a
                  href="https://www.digitalexperience.studio"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-white text-teal-600 px-6 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer"
                >
                  Book Free Consultation
                </a>
                <a
                  href="mailto:letschat@socialswingstudio.com"
                  className="inline-block border-2 border-white text-white px-6 py-3 rounded-full font-bold hover:bg-white hover:text-teal-600 transition-colors text-center whitespace-nowrap cursor-pointer"
                >
                  Email Us
                </a>
              </div>
              <p className="text-sm text-teal-100">
                <strong>Written by Kimberly Herring, Founder of Digital Experience Studio</strong><br />
                Contact: letschat@socialswingstudio.com
              </p>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default GoogleMyBusinessSetup;